import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Btn, Input, Label } from "../components/UI";
import { Modal } from "../components/UI";
import { ShieldCheck, Stethoscope, HeartHandshake, Eye, EyeOff } from "lucide-react";

/* Decorative cream blobs used at the top and bottom of the viewport.
   Pure inline SVG - no external asset needed. Positioned absolutely inside
   the login wrapper so they render on top of the page background but behind
   the login card. */
function BackdropBlobs() {
  return (
    <>
      <svg aria-hidden="true"
        data-testid="login-backdrop-top"
        className="pointer-events-none absolute inset-x-0 top-0 h-72 w-full"
        viewBox="0 0 1440 320" preserveAspectRatio="none">
        <path fill="#E7DFCE" fillOpacity="0.55"
          d="M0,192 C240,128 480,224 720,192 C960,160 1200,96 1440,160 L1440,0 L0,0 Z" />
        <path fill="#DBCFB5" fillOpacity="0.75"
          d="M0,96 C220,32 500,144 780,112 C1020,84 1220,32 1440,80 L1440,0 L0,0 Z" />
        <path fill="#C9B896" fillOpacity="0.55"
          d="M0,48 C260,-16 520,80 780,56 C1020,32 1220,-8 1440,24 L1440,0 L0,0 Z" />
      </svg>
      <svg aria-hidden="true"
        data-testid="login-backdrop-bottom"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-72 w-full"
        viewBox="0 0 1440 320" preserveAspectRatio="none">
        <path fill="#E7DFCE" fillOpacity="0.55"
          d="M0,128 C240,192 480,96 720,128 C960,160 1200,224 1440,160 L1440,320 L0,320 Z" />
        <path fill="#DBCFB5" fillOpacity="0.75"
          d="M0,224 C220,288 500,176 780,208 C1020,236 1220,288 1440,240 L1440,320 L0,320 Z" />
        <path fill="#C9B896" fillOpacity="0.55"
          d="M0,272 C260,336 520,240 780,264 C1020,288 1220,328 1440,296 L1440,320 L0,320 Z" />
      </svg>
    </>
  );
}

/* Reusable password field with show / hide eye toggle. */
function PasswordInput({ value, onChange, testId, placeholder }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <Input data-testid={testId} type={visible ? "text" : "password"} value={value}
        onChange={onChange} placeholder={placeholder} required autoComplete="current-password"
        className="pr-10" />
      <button type="button" onClick={() => setVisible((v) => !v)}
        data-testid={`${testId}-toggle`}
        aria-label={visible ? "Hide password" : "Show password"}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#84675B] hover:text-[#4E342E]">
        {visible ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}

function ForgotPasswordModal({ open, onClose }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [devToken, setDevToken] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      // Dev pods without SMTP receive the reset token in the response so QA
      // can drive the full flow end to end. Production strips this field.
      if (data.dev_reset_token) setDevToken(data.dev_reset_token);
      toast.success(data.message || "If an account exists for that email, a password reset link is on the way.");
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const reset = () => { setEmail(""); setSent(false); setBusy(false); setDevToken(""); onClose(); };

  return (
    <Modal open={open} onClose={reset} title="Forgot Password">
      {sent ? (
        <div data-testid="forgot-password-success" className="space-y-3">
          <div className="rounded-xl border border-[#C8E6C9] bg-[#F1F8E9] p-4 text-sm text-[#2E7D32]">
            If an account exists for <b>{email}</b>, a secure reset link has been sent to that inbox. The link expires in 15 minutes for your protection.
          </div>
          {devToken && (
            <div data-testid="forgot-password-dev-link" className="rounded-xl border border-[#FFCC80] bg-[#FFF3E0] p-3 text-[11px] text-[#6D4C41]">
              <div className="font-bold text-[#8D5A00]">Dev preview only:</div>
              <a href={`/reset-password?token=${devToken}`} target="_blank" rel="noreferrer"
                className="mt-1 block font-mono break-all text-[#4E342E] underline">Open reset link</a>
              <div className="mt-1 text-[10px]">This block never appears in production once SMTP is wired.</div>
            </div>
          )}
          <div className="text-xs text-[#7B6157]">Did not get an email? Contact your clinic administrator, they can trigger a manual reset from the Users page.</div>
          <div className="flex justify-end">
            <Btn data-testid="forgot-password-done-btn" onClick={reset}>Done</Btn>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-[#5D4037]">Enter the email address associated with your account. If it matches, we will email you a secure reset link. For your protection we do not confirm whether the account exists.</p>
          <div>
            <Label>Email</Label>
            <Input data-testid="forgot-password-email" type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)} placeholder="you@clinic.com" />
          </div>
          <div className="flex justify-end gap-2">
            <Btn type="button" variant="ghost" onClick={reset}>Cancel</Btn>
            <Btn data-testid="forgot-password-submit" type="submit" disabled={busy}>
              {busy ? "Sending…" : "Send reset link"}
            </Btn>
          </div>
        </form>
      )}
    </Modal>
  );
}

export default function Login() {
  const { loginSuccess } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { document.title = "Sign In | COCOA EHR"; }, []);
  const [mode, setMode] = useState("staff");
  const [step, setStep] = useState("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [tempToken, setTempToken] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      if (mode === "client") {
        const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/portal/auth/login`, { email, password });
        if (data.requires_2fa) {
          setTempToken(data.temp_token);
          setStep("2fa");
        } else {
          localStorage.setItem("cocoa_portal_token", data.access_token);
          navigate("/portal");
        }
      } else {
        const { data } = await api.post("/auth/login", { email, password });
        if (data.requires_2fa) {
          setTempToken(data.temp_token);
          setStep("2fa");
        } else {
          loginSuccess(data);
        }
      }
    } catch (err) { setError(apiError(err)); }
    setBusy(false);
  };

  const verify = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      if (mode === "client") {
        const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/portal/auth/2fa/verify`, { temp_token: tempToken, code });
        localStorage.setItem("cocoa_portal_token", data.access_token);
        navigate("/portal");
      } else {
        const { data } = await api.post("/auth/verify-2fa", { temp_token: tempToken, code });
        loginSuccess(data);
      }
    } catch (err) { setError(apiError(err)); }
    setBusy(false);
  };

  const QUICK_ACCOUNTS = [
    { label: "Admin", role: "Super Admin", email: "shadab@flynaut.com", password: "CocoaAdmin@2026" },
    { label: "Clinician", role: "Dr. Harper", email: "dr.harper@cocoaehr.com", password: "Clinician@2026" },
    { label: "Front Desk", role: "Maya Brooks", email: "frontdesk@cocoaehr.com", password: "FrontDesk@2026" },
    { label: "Billing", role: "Billing Team", email: "billing@cocoaehr.com", password: "Billing@2026" },
    { label: "Tenant 2", role: "Sunrise BH Admin", email: "admin@sunrisebh.com", password: "Sunrise@2026" },
  ];

  const quickLogin = async (acct) => {
    setError(""); setBusy(true);
    setEmail(acct.email); setPassword(acct.password);
    try {
      const { data } = await api.post("/auth/login", { email: acct.email, password: acct.password });
      if (data.requires_2fa) {
        setTempToken(data.temp_token);
        setStep("2fa");
      } else {
        loginSuccess(data);
      }
    } catch (err) { setError(apiError(err)); }
    setBusy(false);
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#FAF7F2] flex items-center justify-center p-4">
      <BackdropBlobs />

      <div className="relative z-10 w-full max-w-md">
        <div className="mb-6 text-center">
          <img src="/logos/cocoa_ehr_logo_color.webp"
            alt="Cocoa EHR - Good Charting. On Point Tracking. Excellent Care."
            className="mx-auto w-64" data-testid="login-logo" />
        </div>
        <div className="rounded-2xl border border-[#EFEBE0] bg-white/85 p-8 shadow-[0_24px_64px_-24px_rgba(78,52,46,0.28)] backdrop-blur">
          <h1 className="mb-5 text-center font-heading text-xl font-bold text-[#3E2723]" data-testid="login-please-sign-in">
            Please Sign In
          </h1>

          <div className="mb-5 grid grid-cols-2 gap-1 rounded-full bg-[#EFEBE0] p-1">
            <button type="button" data-testid="login-mode-staff" onClick={() => { setMode("staff"); setError(""); }}
              className={`flex items-center justify-center gap-1.5 rounded-full px-3 py-2 text-xs font-bold transition-colors ${mode === "staff" ? "bg-[#4E342E] text-white shadow" : "text-[#6D4C41]"}`}>
              <Stethoscope size={13} /> Clinic Staff
            </button>
            <button type="button" data-testid="login-mode-client" onClick={() => { setMode("client"); setError(""); setStep("credentials"); }}
              className={`flex items-center justify-center gap-1.5 rounded-full px-3 py-2 text-xs font-bold transition-colors ${mode === "client" ? "bg-[#4E342E] text-white shadow" : "text-[#6D4C41]"}`}>
              <HeartHandshake size={13} /> Client Portal
            </button>
          </div>

          {step === "credentials" ? (
            <form onSubmit={submit} className="space-y-4">
              <h2 className="font-heading text-sm font-semibold text-[#6D4C41]">
                {mode === "client" ? "Sign in to your client portal" : "Sign in to your clinic"}
              </h2>
              <div>
                <Label>Email</Label>
                <Input data-testid="login-email-input" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)} placeholder="you@clinic.com"
                  required autoComplete="username" />
              </div>
              <div>
                <Label>Password</Label>
                <PasswordInput testId="login-password-input" value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
                <div className="mt-1 text-right">
                  <button type="button" onClick={() => setForgotOpen(true)}
                    data-testid="login-forgot-password-link"
                    className="text-xs font-medium text-[#7B6157] hover:text-[#4E342E] hover:underline">
                    Forgot Password?
                  </button>
                </div>
              </div>
              {error && <p data-testid="login-error" className="text-sm text-[#C62828]">{error}</p>}
              <Btn data-testid="login-submit-btn" type="submit" disabled={busy} className="w-full h-11">{busy ? "Signing in…" : "Sign in"}</Btn>
              {mode === "client" ? (
                <>
                  <button type="button" disabled={busy} data-testid="quick-login-client-demo"
                    onClick={() => { setEmail("leila.h@mail.com"); setPassword("PortalTest@2026"); }}
                    className="w-full rounded-xl border border-[#E5DFD3] bg-white px-3 py-2.5 text-left transition-colors hover:bg-[#F5F2EB] hover:border-[#D7CCC8] disabled:opacity-50">
                    <div className="text-sm font-semibold text-[#3E2723]">Demo Client</div>
                    <div className="text-[11px] text-[#84675B]">Leila Hassan &middot; click to fill, then Sign in</div>
                  </button>
                  <p className="text-center text-xs text-[#84675B]">Do not have access yet? Ask your clinic to invite you to the portal.</p>
                </>
              ) : (<>
              <div className="flex items-center gap-3 pt-1">
                <span className="h-px flex-1 bg-[#EFEBE0]" />
                <span className="text-[11px] font-semibold uppercase tracking-wider text-[#84675B]">Quick access</span>
                <span className="h-px flex-1 bg-[#EFEBE0]" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                {QUICK_ACCOUNTS.map((a) => (
                  <button key={a.label} type="button" disabled={busy}
                    data-testid={`quick-login-${a.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => quickLogin(a)}
                    className="rounded-xl border border-[#E5DFD3] bg-white px-3 py-2.5 text-left transition-colors hover:bg-[#F5F2EB] hover:border-[#D7CCC8] disabled:opacity-50">
                    <div className="text-sm font-semibold text-[#3E2723]">{a.label}</div>
                    <div className="text-[11px] text-[#84675B]">{a.role}</div>
                  </button>
                ))}
              </div>
              <p className="text-center text-xs text-[#84675B]">HIPAA-aligned &middot; All access is audit-logged</p>
              </>)}
            </form>
          ) : (
            <form onSubmit={verify} className="space-y-4">
              <div className="flex items-center gap-2 text-[#3E2723]">
                <ShieldCheck size={20} className="text-[#7B6157]" />
                <h2 className="font-heading text-lg font-bold">Two-factor verification</h2>
              </div>
              <p className="text-sm text-[#6D4C41]">Enter the 6-digit code from your authenticator app.</p>
              <Input data-testid="login-2fa-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" maxLength={6} className="text-center text-lg tracking-[0.4em]" required />
              {error && <p data-testid="login-2fa-error" className="text-sm text-[#C62828]">{error}</p>}
              <Btn data-testid="login-2fa-submit-btn" type="submit" disabled={busy} className="w-full h-11">{busy ? "Verifying…" : "Verify"}</Btn>
              <button type="button" data-testid="login-2fa-back-btn" onClick={() => setStep("credentials")} className="w-full text-center text-sm text-[#7B6157] hover:underline">Back to sign in</button>
            </form>
          )}
        </div>
      </div>

      {/* Bottom-left / bottom-right legal links */}
      <Link to="/legal/terms" data-testid="login-terms-link"
        className="fixed bottom-4 left-6 text-xs font-medium text-[#7B6157] hover:text-[#4E342E] hover:underline">
        Terms &amp; Conditions
      </Link>
      <Link to="/legal/privacy" data-testid="login-privacy-link"
        className="fixed bottom-4 right-6 text-xs font-medium text-[#7B6157] hover:text-[#4E342E] hover:underline">
        Privacy Policy
      </Link>

      <ForgotPasswordModal open={forgotOpen} onClose={() => setForgotOpen(false)} />
    </div>
  );
}
